import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import moment from 'moment';
import { QzTrayService } from '../qz-tray.service';
import { MasterDataSeriveService } from './master-data-serive.service';
import { Labels, ProductionHistory } from '../Models/ApplicationModels';
import { LabelEntityType } from './enum';

@Injectable({ providedIn: 'root' })
export class LabelPrintingService {
  private formatLabels: Labels[] = [];
  private activeTag: number = 0;
  private previewZplCode: string = '';
  private previewTagSize: string = '';
  private printerType: number = 0;

  constructor(
    private printersService: QzTrayService,
    private masterservice: MasterDataSeriveService
  ) {}

  async initPrinter(): Promise<void> {
    await this.printersService.initQZ();
  }

  loadProductLabelFormats(companyId: number): Observable<Labels[]> {
    return this.masterservice.getAllLabels(companyId).pipe(
      map((res: any) => {
        const data: Labels[] = Array.isArray(res) ? res : [res];
        return data.filter(
          (label) => label.labelEntityType === LabelEntityType.Product
        );
      }),
      tap((labels) => (this.formatLabels = labels))
    );
  }

  printHistoryLabel(barcode: string, includeDeleted: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      this.masterservice.printHistoryLabel(barcode, includeDeleted).subscribe({
        next: (res) => {
          const printHistory = res as ProductionHistory[];
          const data = printHistory[0];
          if (!data) {
            resolve();
            return;
          }

          printHistory.forEach((first) => {
            for (let i = 0; i < first.numberOfPieces; i++) {
              this.printerType = data.labelType;
              if (data.brandTypeId == 1) {
                this.generateEcomTag(
                  data.departmentDescr,
                  data.categoryName,
                  data.brandDescr,
                  data.price.toString(),
                  '0',
                  data.numberOfPieces.toString()
                );
              } else if (data.brandTypeId == 2) {
                this.generateBoutiqueTag(
                  data.departmentDescr,
                  data.categoryName,
                  data.brandDescr,
                  data.price.toString(),
                  data.productionStoreId.toString(),
                  data.totalQty.toString()
                );
              } else {
                this.activeTag = data.formatTypeId;
                const storeId = JSON.parse(localStorage.getItem('storeId')!);
                const zplData = this.generateZpl(
                  data.companyName,
                  data.departmentDescr,
                  data.categoryName,
                  data.subCategoryName,
                  'Size:',
                  data.sizeDescr,
                  data.price.toFixed(2).toString(),
                  data.barcode,
                  storeId,
                  data.qualityDescr,
                  (i + 1).toString(),
                  first.numberOfPieces.toString(),
                  first.displayName,
                  first?.weekNumber ?? 0,
                  first.brandDescr
                );
                this.printLabel(zplData);
              }
            }
          });

          resolve();
        },
        error: (err) => reject(err)
      });
    });
  }

  private generateZpl(
    CompanyName: string,
    Department: string,
    Category: string,
    SubCategory: string,
    SizeText: string = 'Size',
    SizeSize: string,
    Price: string,
    BarcodeValue: string,
    CompanyNameLine2: string,
    qualityDescr: string = '',
    LabelIndex: string,
    Piece: string,
    colorName: string,
    weekNumber: number = 0,
    Brand: string = ''
  ): string[] {
    if (SizeSize == null) {
      SizeText = ' ';
    }
    if (Department == Category) {
      Category = ' ';
    }

    function safeParse(value: string | null): string {
      try {
        return value ? JSON.parse(value) : '';
      } catch {
        return value ?? '';
      }
    }

    let storeRaw =
      localStorage.getItem('storeName') ??
      localStorage.getItem('defaultStoreName') ??
      '';
    let Store: string = safeParse(storeRaw);
    let Color = colorName;
    let WeekNumber = weekNumber.toString();
    if (
      Brand == null ||
      Brand == undefined ||
      Brand == 'None' ||
      Brand == 'Select Brand'
    ) {
      Brand = '';
    }
    const currentDate = moment();
    const Date = currentDate.format('MM/DD');
    const Date2 = currentDate.format('MM/DD/YYYY');
    const Time = currentDate.format('HH:mm');

    let userStorageData = this.masterservice.getUserDetailsFromStorage();
    const User = userStorageData?.brijjusername || '';
    const DisplayName = userStorageData?.displayName || '';

    this.formatLabels.map((res) => {
      if (res.id == this.activeTag) {
        this.previewZplCode = res.zplCode;
        this.previewTagSize = this.extractLabelSize(res.name);
      }
    });

    let QualityDescr = qualityDescr ? qualityDescr : '';

    if (typeof this.previewZplCode !== 'string') {
      console.error('Invalid or missing ZPL code');
      return [];
    }

    const replacedZplCode = this.previewZplCode
      .replace(/\${CompanyName}/g, CompanyName ? CompanyName : '')
      .replace(/\${Color}/g, Color ? Color : '')
      .replace(/\${WeekNumber}/g, WeekNumber ? WeekNumber : '')
      .replace(/\${Department}/g, Department ? Department : '')
      .replace(/\${Category}/g, Category ? Category : '')
      .replace(/\${SubCategory}/g, SubCategory ? SubCategory : '')
      .replace(/\${SizeText}/g, SizeText ? SizeText : '')
      .replace(/\${SizeSize}/g, SizeSize ? SizeSize : '')
      .replace(/\${Price}/g, Price ? Price : '')
      .replace(/\${Date}/g, Date ? Date : '')
      .replace(/\${Date2}/g, Date2 ? Date2 : '')
      .replace(/\${Time}/g, Time)
      .replace(/\${User}/g, User)
      .replace(/\${Name}/g, DisplayName)
      .replace(/\${DisplayName}/g, DisplayName)
      .replace(/\${BarcodeValue}/g, BarcodeValue ? BarcodeValue : '')
      .replace(/\${Store}/g, Store ? Store : '')
      .replace(/\${Quality}/g, QualityDescr ? QualityDescr : '')
      .replace(/\${LabelIndex}/g, LabelIndex ? LabelIndex : '')
      .replace(/\${Piece}/g, Piece ? Piece : '')
      .replace(/\${Brand}/g, Brand ? Brand : '')
      .replace(
        /\${CompanyNameLine2}/g,
        CompanyNameLine2 ? CompanyNameLine2 : ''
      );

    return replacedZplCode.split('\n').map((line) => line.trim());
  }

  private generateEcomTag(
    department?: string,
    category?: string,
    brand?: string,
    price?: string,
    price2?: string,
    qty?: string
  ): string[] {
    const zplTemplate: string[] = [
      '^XA',
      '~TA000',
      '~JSN',
      '^LT0',
      '^MNW',
      '^MTD',
      '^PON',
      '^PMN',
      '^LH0,0',
      '^JMA',
      '^PR4,4',
      '~SD15',
      '^JUS',
      '^LRN',
      '^CI27',
      '^PA0,1,1,0',
      '^XZ',
      '^XA',
      '^MMT',
      '^PW305',
      '^LL508',
      '^LS0',
      `^FT44,150^A0N,28,28^FH^CI28^FDE-Commerce^FS^CI27`,
      `^FT44,200^A0N,22,22^FH^CI28^FD${department}/${category}^FS^CI27`,
      `^FT44,250^A0N,28,28^FH^CI28^FD${brand}^FS^CI27`,
      `^FT44,300^A0N,22,22^FH^CI28^FDGoodwill Finds $${price} ^FS^CI27`,
      `^FT44,335^A0N,22,22^FH^CI28^FDRCS AI Avg^FS^CI27`,
      `^FT185,335^A0N,22,22^FH^CI28^FD $ ${price2} ^FS^CI27`,
      `^FT44,400^A0N,22,22^FH^CI28^FDCurent OnHand^FS^CI27`,
      `^FT185,400^A0N,22,22^FH^CI28^FD: ${qty} ^FS^CI27`,
      '^PQ1,0,1,Y',
      '^XZ'
    ];
    this.printLabel(zplTemplate);
    return zplTemplate;
  }

  private generateBoutiqueTag(
    department?: string,
    category?: string,
    brand?: string,
    price?: string,
    storeId?: string,
    qty?: string
  ): string[] {
    const zplTemplate: string[] = [
      '^XA',
      '~TA000',
      '~JSN',
      '^LT0',
      '^MNW',
      '^MTD',
      '^PON',
      '^PMN',
      '^LH0,0',
      '^JMA',
      '^PR4,4',
      '~SD15',
      '^JUS',
      '^LRN',
      '^CI27',
      '^PA0,1,1,0',
      '^XZ',
      '^XA',
      '^MMT',
      '^PW305',
      '^LL508',
      '^LS0',
      `^FT44,150^A0N,28,28^FH^CI28^FDBoutique Item^FS^CI27`,
      `^FT44,200^A0N,22,22^FH^CI28^FD${department}/${category}^FS^CI27`,
      `^FT44,250^A0N,28,28^FH^CI28^FD${brand}^FS^CI27`,
      `^FT44,300^A0N,22,22^FH^CI28^FDStoreID ^FS^CI27`,
      `^FT134,300^A0N,22,22^FH^CI28^FDQty ^FS^CI27`,
      `^FT184,300^A0N,22,22^FH^CI28^FDAvg Price^FS^CI27`,
      `^FT34,335^A0N,22,22^FH^CI28^FD ${storeId} ^FS^CI27`,
      `^FT124,335^A0N,22,22^FH^CI28^FD ${qty} ^FS^CI27`,
      `^FT184,335^A0N,22,22^FH^CI28^FD $ ${price} ^FS^CI27`,
      '^PQ1,0,1,Y',
      '^XZ'
    ];
    this.printLabel(zplTemplate);
    return zplTemplate;
  }

  private extractLabelSize(input: string): string {
    const labelNumber = input.match(/[0-9]+(\.[0-9]+)?/)?.[0] || '';
    const sizeNumber = input.match(/X\s?([0-9]+(\.[0-9]+)?)"/)?.[1] || '';
    return `${labelNumber}x${sizeNumber}`;
  }

  private printLabel(data: any): void {
    let printerName: string = '';

    if (this.printerType == 1) {
      printerName = 'ZebraLabel';
    } else if (this.printerType == 2) {
      printerName = 'ZebraTag';
    } else if (this.printerType == 3) {
      printerName = 'ZebraBoth';
    }
    this.printersService.printData(printerName, data);
  }
}
